/**
 * PublicAppContext — stores application-level branding (logo, banner_image, app_name)
 * for all public pages under /p/*.
 *
 * Any public page that receives `app_branding` from its API response should call
 * `setAppBranding` to make it available to PublicLayout (navbar logo, banner).
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PublicAppContext = createContext({
  appBranding: null,
  setAppBranding: () => {},
  crumbLabel: null,
  setCrumbLabel: () => {},
});

export function PublicAppProvider({ children }) {
  const [appBranding, setAppBranding] = useState(null);
  // { path, label } — a human name for the current page's own breadcrumb crumb.
  // Stored with the path it was set for so a stale title from the previous page
  // can never leak onto the next one.
  const [crumbLabel, setCrumbLabel] = useState(null);

  // app_branding.theme is deliberately NOT applied. The admin records its own
  // UI theme there when the public index config is saved, so honouring it
  // painted every public page in whatever theme the admin happened to be using
  // — and the admin has no Agni theme to choose. This client's colours come
  // from its own ThemeProvider default (or the visitor's saved choice).

  return (
    <PublicAppContext.Provider value={{ appBranding, setAppBranding, crumbLabel, setCrumbLabel }}>
      {children}
    </PublicAppContext.Provider>
  );
}

export function usePublicApp() {
  return useContext(PublicAppContext);
}

/**
 * useCrumbLabel — lets a public page name its own breadcrumb crumb, so the
 * trail reads "… › Loan Applications › Acme Corp" instead of "… › 42".
 * Pass a falsy value while the record is still loading; the crumb falls back to
 * the URL segment until a real label arrives.
 */
export function useCrumbLabel(label) {
  const { setCrumbLabel } = usePublicApp();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!setCrumbLabel || label === null || label === undefined || label === '') return;
    setCrumbLabel({ path: pathname, label: String(label) });
  }, [label, pathname, setCrumbLabel]);
}
